/**
 * The Arche settings card: how the arche will appear once published — icon,
 * name, short description, price and visibility — with "Edit" opening the
 * settings dialog. Until the owner saves settings, it shows what the forge
 * would publish from the arche's current details.
 */
import { Link } from 'react-router-dom';
import { ImageOff, Loader2, Settings } from 'lucide-react';
import type { ForgePublishSettingsView } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { describeArcheSettings, upcomingArcheIcon } from './archeSettingsSummary';

interface ForgeArcheSettingsCardProps {
  view: ForgePublishSettingsView | null;
  isLoading: boolean;
  onEdit: () => void;
  /** The forge's arche — once it is live, a link to its extra Store listings. */
  archeId?: string;
}

export function ForgeArcheSettingsCard({
  view,
  isLoading,
  onEdit,
  archeId,
}: ForgeArcheSettingsCardProps) {
  const icon = view ? upcomingArcheIcon(view) : null;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="forge-arche-settings">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Arche settings</h3>
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Settings className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </div>
      {!view ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {isLoading ? 'Loading…' : 'Name, icon, price and how the arche appears.'}
        </p>
      ) : (
        <>
          <div className="flex items-start gap-2.5">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border bg-muted">
              {icon ? (
                <img src={icon} alt="Arche icon" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <ImageOff className="h-4 w-4" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{view.settings.name || 'No name yet'}</p>
              <p className="line-clamp-2 text-xs text-muted-foreground">
                {view.settings.description || 'No description yet'}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="forge-arche-settings-summary">
            {describeArcheSettings(view.settings).join(' · ')}
          </p>
          {!view.saved && (
            <p className="text-xs text-muted-foreground">
              From the arche&apos;s current details. Edit to change them.
            </p>
          )}
          {archeId && view.archeStatus === 'published' && (
            <p className="text-xs">
              <Link
                to={`/contributor/arches/${archeId}#store-listings`}
                className="text-primary underline"
              >
                Store listings
              </Link>{' '}
              <span className="text-muted-foreground">
                — give games inside this arche their own place in the Store.
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
